import { useMemo } from 'react';

import { addBignumbers, bn } from 'tools';
import { IAccountBalances, ICexAccount } from 'types/accounts';
import { ICexAccountShortTableRow } from 'types/tables';
import { formatToken } from 'utils/formats/formatToken';

interface IAccountWithBalance extends ICexAccount, Partial<IAccountBalances> {}

const useArbitrageCexAccountsRecords = (pairAccounts: IAccountWithBalance[]) => {
  const tableData = useMemo<ICexAccountShortTableRow[]>(() => {
    const rows = (pairAccounts ?? [])
      .filter(r => !r.is_disabled)
      .map(account => {
        const quoteTotal = bn(account.quote_balance ?? '0', 18).add(
          bn(account.quote_balance_locked ?? '0', 18),
        );
        const baseTotal = bn(account.base_balance ?? '0', 18).add(
          bn(account.base_balance_locked ?? '0', 18),
        );

        return { account, quoteTotal, baseTotal };
      });

    rows.sort((a, b) => {
      if (a.baseTotal.eq(b.baseTotal)) {
        if (a.quoteTotal.eq(b.quoteTotal)) return 0;
        return a.quoteTotal.gt(b.quoteTotal) ? -1 : 1;
      }

      return a.baseTotal.gt(b.baseTotal) ? -1 : 1;
    });

    return rows.map(({ account }) => {
      const tokenBase = formatToken(
        addBignumbers(
          [bn(account.base_balance ?? '0'), 18],
          [bn(account.base_balance_locked ?? '0'), 18],
        ),
        18,
      );

      const tokenQuote = formatToken(
        addBignumbers(
          [bn(account.quote_balance ?? '0'), 18],
          [bn(account.quote_balance_locked ?? '0'), 18],
        ),
        18,
      );

      const notesCol = {
        value: account.notes as string,
      };

      return {
        tokenBaseCol: tokenBase,
        tokenQuoteCol: tokenQuote,
        notesCol,
        id: account.id,
        cexCol: account.cex,
      } as ICexAccountShortTableRow;
    });
  }, [pairAccounts]);

  return { tableData };
};

export { useArbitrageCexAccountsRecords };
